import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, EmptyState } from "@/components/common";
import { LevelBadge } from "@/components/LevelBadge";
import { AdminLayout, btn, btnGhost, inp, lbl } from "@/pages/admin/adminShared";
import { formatDate, todayISO } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Search, ChevronUp, ChevronDown, X, Copy, Send, Save, CheckCircle2, ListMusic } from "lucide-react";

const PLAYLIST_TAGS = ["New", "Recap", "Request", "Warm-up", "Cool-down"];

function PlaylistBuilder({ session, dances }) {
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [published, setPublished] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/admin/sessions/${session.id}/playlist`);
    setItems(data.items.map((it) => ({ dance: it.dance, note: it.note, plays: it.plays || 1, tag: it.tag || "" })));
    setNotes(data.notes || ""); setPublished(data.published); setLoading(false);
  };
  useEffect(() => { load(); }, [session.id]);

  const inList = new Set(items.map((it) => it.dance.id));
  const filtered = (dances || []).filter((d) => !inList.has(d.id) && (!q || d.name.toLowerCase().includes(q.toLowerCase())));
  const add = (d) => setItems((p) => [...p, { dance: d, note: "", plays: 1, tag: "" }]);
  const remove = (id) => setItems((p) => p.filter((it) => it.dance.id !== id));
  const move = (i, dir) => setItems((p) => { const a = [...p]; const j = i + dir; if (j < 0 || j >= a.length) return p; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const setField = (i, key, val) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, [key]: val } : it));

  const save = async (publish) => {
    try {
      await api.put(`/admin/sessions/${session.id}/playlist`, { notes, items: items.map((it, i) => ({ dance_id: it.dance.id, position: i, note: it.note || "", plays: Number(it.plays) || 1, tag: it.tag || "" })) });
      if (publish !== undefined) { await api.post(`/admin/sessions/${session.id}/playlist/publish?publish=${publish}`); setPublished(publish); }
      toast.success(publish === true ? "Playlist published" : publish === false ? "Playlist unpublished" : "Playlist saved");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const copyPrevious = async () => {
    try { const { data } = await api.post(`/admin/sessions/${session.id}/playlist/copy-previous`); setItems(data.items.map((it) => ({ dance: it.dance, note: it.note, plays: it.plays || 1, tag: it.tag || "" }))); toast.success("Copied previous playlist"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (loading) return <Spinner />;
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display font-bold text-[#2D1E18]">Playlist ({items.length})</h3>
          {published ? <span className="rounded-full bg-[#E8F0EC] px-2 py-0.5 text-xs font-bold uppercase text-[#4A6B5D]">Published</span> : <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold uppercase text-[#6E5B51]">Draft</span>}
        </div>
        {items.length === 0 ? <EmptyState title="Empty playlist" subtitle="Add dances from the library →" icon={ListMusic} /> : (
          <ol className="space-y-2">
            {items.map((it, i) => (
              <li key={it.dance.id} className="rounded-xl border border-border bg-card p-2.5" data-testid={`playlist-item-${it.dance.slug}`}>
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#C85A32] text-xs font-bold text-white">{i + 1}</span>
                  <span className="flex-1 font-semibold text-[#2D1E18]">{it.dance.name}</span>
                  <LevelBadge level={it.dance.level} />
                  <button onClick={() => move(i, -1)} aria-label="Move up" className="rounded-lg p-2.5 text-[#6E5B51] hover:bg-secondary"><ChevronUp size={16} /></button>
                  <button onClick={() => move(i, 1)} aria-label="Move down" className="rounded-lg p-2.5 text-[#6E5B51] hover:bg-secondary"><ChevronDown size={16} /></button>
                  <button onClick={() => remove(it.dance.id)} aria-label="Remove" className="rounded-lg p-2.5 text-[#6E5B51] hover:text-[#991B1B]"><X size={16} /></button>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 pl-8">
                  <div className="flex items-center gap-1">
                    {PLAYLIST_TAGS.map((t) => (
                      <button key={t} type="button" onClick={() => setField(i, "tag", it.tag === t ? "" : t)} data-testid={`playlist-tag-${it.dance.slug}-${t.toLowerCase()}`}
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${it.tag === t ? "bg-[#2D1E18] text-white" : "border border-border bg-white text-[#6E5B51] hover:border-[#C85A32] hover:text-[#B04B26]"}`}>{t}</button>
                    ))}
                  </div>
                  <label className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-[#6E5B51]">
                    Played
                    <input type="number" min={1} value={it.plays} onChange={(e) => setField(i, "plays", Math.max(1, Number(e.target.value) || 1))}
                      data-testid={`playlist-plays-${it.dance.slug}`} className="w-14 rounded-lg border border-border bg-white px-2 py-1 text-center text-sm text-[#2D1E18] outline-none focus:border-[#C85A32]" />
                    ×
                  </label>
                </div>
              </li>
            ))}
          </ol>
        )}
        <textarea className={`${inp} mt-3`} rows={2} placeholder="Playlist notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="playlist-notes" />
        <div className="mt-3 flex flex-wrap gap-2 sticky bottom-16 z-10 -mx-2 rounded-xl bg-card/95 px-2 py-2 backdrop-blur md:static md:mx-0 md:bg-transparent md:p-0 md:backdrop-blur-0">
          <button className={btn} onClick={() => save()} data-testid="playlist-save"><Save size={15} /> Save</button>
          {published ? <button className={btnGhost} onClick={() => save(false)} data-testid="playlist-unpublish">Unpublish</button>
            : <button className={btnGhost} onClick={() => save(true)} data-testid="playlist-publish"><Send size={15} /> Publish</button>}
          <button className={btnGhost} onClick={copyPrevious} data-testid="playlist-copy-previous"><Copy size={15} /> Copy previous</button>
        </div>
      </div>
      <div>
        <h3 className="mb-3 font-display font-bold text-[#2D1E18]">Dance Library</h3>
        <div className="flex items-center gap-2 rounded-xl border border-border px-3"><Search size={16} className="text-[#6E5B51]" /><input className="w-full bg-transparent py-2 outline-none" placeholder="Search dances" value={q} onChange={(e) => setQ(e.target.value)} data-testid="playlist-dance-search" /></div>
        <div className="mt-3 max-h-96 space-y-1 overflow-y-auto">
          {filtered.map((d) => (
            <button key={d.id} onClick={() => add(d)} data-testid={`add-to-playlist-${d.slug}`} className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left hover:bg-secondary">
              <span className="flex items-center gap-2"><span className="font-semibold text-[#2D1E18]">{d.name}</span><LevelBadge level={d.level} /></span>
              <Plus size={16} className="text-[#B04B26]" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function CompleteSession({ session }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const ACTS = ["danced", "taught", "reviewed"];

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/admin/sessions/${session.id}/playlist`);
      setResults(data.items.map((it) => ({ dance: it.dance, activities: ["danced"] })));
      setLoading(false);
    })();
  }, [session.id]);

  const toggle = (i, act) => setResults((p) => p.map((r, idx) => idx !== i ? r : { ...r, activities: r.activities.includes(act) ? r.activities.filter((a) => a !== act) : [...r.activities, act] }));
  const allDanced = () => setResults((p) => p.map((r) => ({ ...r, activities: Array.from(new Set([...r.activities, "danced"])) })));
  const save = async () => {
    try { await api.post(`/admin/sessions/${session.id}/complete`, { results: results.map((r) => ({ dance_id: r.dance.id, activities: r.activities })) }); toast.success("Session completed — history updated!"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (loading) return <Spinner />;
  if (!results.length) return <EmptyState title="No planned playlist" subtitle="Build a playlist first, then record what actually happened." />;
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-[#5C4A42]">Tick what actually happened for each dance.</p>
        <button className={btnGhost} onClick={allDanced} data-testid="mark-all-danced">Mark all danced</button>
      </div>
      <div className="space-y-2">
        {results.map((r, i) => (
          <div key={r.dance.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card p-3" data-testid={`complete-row-${r.dance.slug}`}>
            <span className="font-semibold text-[#2D1E18]">{r.dance.name}</span>
            <div className="flex gap-1.5">
              {ACTS.map((a) => (
                <button key={a} onClick={() => toggle(i, a)} data-testid={`activity-${r.dance.slug}-${a}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize ${r.activities.includes(a) ? "bg-[#C85A32] text-white" : "border border-border bg-white text-[#5C4A42]"}`}>{a}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button className={`${btn} mt-4`} onClick={save} data-testid="complete-session-save"><CheckCircle2 size={16} /> Save session history</button>
    </div>
  );
}

export default function ClassSessions() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [classId, setClassId] = useState("");
  const [newDate, setNewDate] = useState(todayISO());
  const [activeSession, setActiveSession] = useState(null);

  const { data: classes } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/classes")).data });
  const { data: dances } = useQuery({ queryKey: ["admin-dances"], queryFn: async () => (await api.get("/dances?limit=1000")).data });
  const { data: sessions, refetch } = useQuery({ queryKey: ["sessions", classId], enabled: !!classId, queryFn: async () => (await api.get(`/admin/classes/${classId}/sessions`)).data });

  const createSession = async () => {
    try { const { data } = await api.post("/admin/sessions", { class_id: classId, date: newDate }); toast.success("Session ready"); refetch(); setActiveSession(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout role={user?.role} title="Sessions &amp; Playlists" subtitle="Prepare tonight's playlist, then record what was actually taught after class.">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-border bg-card p-5">
            <label className={lbl}>Class</label>
            <select className={inp} value={classId} onChange={(e) => { setClassId(e.target.value); setActiveSession(null); }} data-testid="session-class-select">
              <option value="">Select a class…</option>
              {(classes || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {classId && (
              <div className="mt-4">
                <label className={lbl}>New session date</label>
                <div className="flex gap-2">
                  <input type="date" className={inp} value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="session-date" />
                  <button className={btn} onClick={createSession} data-testid="create-session"><Plus size={15} /></button>
                </div>
              </div>
            )}
            {classId && (
              <div className="mt-4">
                <p className={lbl}>Sessions</p>
                <div className="space-y-1">
                  {(sessions || []).map((s) => (
                    <button key={s.id} onClick={() => setActiveSession(s)} data-testid={`session-${s.id}`}
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${activeSession?.id === s.id ? "bg-[#FBEBE4] font-semibold text-[#B04B26]" : "hover:bg-secondary text-[#5C4A42]"}`}>
                      <span>{formatDate(s.date, { weekday: "short", year: true })}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs ${s.status === "completed" ? "bg-[#E8F0EC] text-[#4A6B5D]" : "bg-secondary text-[#6E5B51]"}`}>{s.status}</span>
                    </button>
                  ))}
                  {sessions && !sessions.length && <p className="text-sm text-[#6E5B51]">No sessions yet — create one above.</p>}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          {!activeSession ? <EmptyState title="Select or create a session" subtitle="Pick a class and date to build a playlist." icon={ListMusic} /> : (
            <div className="rounded-2xl border border-border bg-card p-5">
              <h2 className="mb-4 font-display text-lg font-bold text-[#2D1E18]">{formatDate(activeSession.date, { year: true })}</h2>
              <Tabs defaultValue="playlist">
                <TabsList data-testid="session-tabs">
                  <TabsTrigger value="playlist">Build Playlist</TabsTrigger>
                  <TabsTrigger value="complete">Complete Session</TabsTrigger>
                </TabsList>
                <TabsContent value="playlist" className="mt-4"><PlaylistBuilder session={activeSession} dances={dances} /></TabsContent>
                <TabsContent value="complete" className="mt-4"><CompleteSession session={activeSession} /></TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
